/**
 * 🐋 Advertiser Landing - Cetalingua FM
 * Target path: /src/components/advertiser/AdvertiserLanding.js
 * REPLACES existing AdvertiserLanding.js
 * 
 * CHANGES FROM ORIGINAL:
 * - "14 Whale Species" → "88 Marine Species"
 * - Benefits: "Monthly push notifications" → "Weekly push notifications"
 * - Benefits: "Create campaigns & offers" → "Featured Media, What's New, Hot Deals"
 * - "What You Can Promote" section: 4 old types → 3 new content pillars
 * - Free plan: "Create campaigns & offers" → "Monthly content updates"
 * - Paid plan: "Unlimited listings" → "Weekly content updates"
 * - Paid plan: "Monthly push notifications" → "Weekly push notifications"
 * - How It Works step 3: "Create Listings" → "Post Content"
 * - FAQ updated for new content model and weekly cadence
 * - All pricing/structure unchanged
 */

import React, { useState } from 'react';
import './AdvertiserLanding.css';
import PublicReach, { SpeciesLookup } from './PublicReach';
import Showcase from '../common/Showcase';

const SHOTS = [
  { file: 'adv-app-featured.png', kind: 'phone', caption: 'Your featured post in the Cetalingua FM app' },
  { file: 'adv-app-deals.png', kind: 'phone', caption: "Hot deals and what's new from your company" },
  { file: 'adv-app-push.png', kind: 'phone', caption: 'Your monthly push notification on listeners\' phones' },
  { file: 'adv-portal-dashboard.png', kind: 'desktop', caption: 'Your self-service dashboard: posts, deals, push' },
];

const AdvertiserLanding = ({ onSelectPlan, onNavigate }) => {
  const [selectedPrepaid, setSelectedPrepaid] = useState('12-month');

  const handlePrepaidClick = (plan) => {
    setSelectedPrepaid(plan);
  };

  return (
    <div className="landing-container">
      {/* Hero Section */}
      <div className="hero-section">
        <div className="hero-content">
          <span className="hero-logo">🐋</span>
          <h1 className="hero-title">Reach Whale Enthusiasts Worldwide</h1>
          <p className="hero-subtitle">
            Promote your whale watching tours to passionate marine life lovers on Cetalingua FM
          </p>
          {/* 14 Oct 2026: audience numbers block (10K+ users, 50+ stations) removed at the user's request */}
          {/* 26 Oct 2026: LIVE numbers instead — shown automatically from 10 app users (rounded down) */}
          <PublicReach />
        </div>
      </div>

      {/* What is Cetalingua Section */}
      <div className="about-section">
        <h2>What is Cetalingua FM?</h2>
        <p className="about-text">
          Cetalingua FM is a mobile app that lets users listen to live whale songs from hydrophone stations around the world. 
          Our users are passionate about marine life – they're exactly the audience that wants to experience whales in person.
        </p>
        <div className="about-features">
          <div className="about-feature">
            <span className="feature-emoji">🎧</span>
            <p>Live whale songs 24/7</p>
          </div>
          <div className="about-feature">
            <span className="feature-emoji">🌍</span>
            <p>Global hydrophone network</p>
          </div>
          <div className="about-feature">
            <span className="feature-emoji">🐋</span>
            <p>88 marine species tracked</p>
          </div>
        </div>
      </div>

      <Showcase intro="What your customers see in the app – and where you manage it." items={SHOTS} />

      {/* Benefits Section */}
      <div className="benefits-section">
        <h2>Why Advertise with Us?</h2>
        <div className="benefits-grid">
          <div className="benefit-card">
            <div className="benefit-icon">🎯</div>
            <h3>Species-Based Targeting</h3>
            <p>Your content reaches users who selected interest in the species you feature on your tours – across 88 marine species</p>
          </div>
          <div className="benefit-card">
            <div className="benefit-icon">🔥</div>
            <h3>Highly Engaged Audience</h3>
            <p>Our users spend hours listening to whale songs – they're passionate and ready to book real experiences</p>
          </div>
          <div className="benefit-card">
            <div className="benefit-icon">📸</div>
            <h3>Rich Content</h3>
            <p>Share Featured Media, What's New updates, and Hot Deals – photos, videos, promo codes, and booking links</p>
          </div>
          <div className="benefit-card">
            <div className="benefit-icon">🔔</div>
            <h3>Monthly Push Notification</h3>
            <p>Send one push notification a month to species-matched users with your latest content (paid plans)</p>
          </div>
          <div className="benefit-card">
            <div className="benefit-icon">📊</div>
            <h3>Self-Service Dashboard</h3>
            <p>Post featured media, news updates, and deals anytime from your dashboard</p>
          </div>
          <div className="benefit-card">
            <div className="benefit-icon">✅</div>
            <h3>Quality Network</h3>
            <p>Admin-approved content ensures your company appears alongside quality operators</p>
          </div>
        </div>
      </div>

      {/* Live: how many app users follow each species (26 Oct 2026) */}
      <SpeciesLookup />

      {/* Pricing Section */}
      <div className="pricing-section">
        <h2>Simple, Affordable Pricing</h2>
        <p className="pricing-subtitle">Choose the plan that works for your business</p>
        
        <div className="pricing-grid">
          {/* Free Plan */}
          <div className="pricing-card free-plan">
            <div className="plan-badge">FREE PROGRAM</div>
            <h3>Sticker Partner</h3>
            <div className="price">
              <span className="price-amount">$0</span>
            </div>
            <p className="price-period">3 months of advertising</p>
            
            <div className="plan-features">
              <h4>How it works:</h4>
              <ul>
                <li>✓ We send you Cetalingua FM stickers/flyers</li>
                <li>✓ Display them on your boats, office, or give to customers</li>
                <li>✓ Upload a photo showing distribution</li>
                <li>✓ Get 3 months free advertising!</li>
              </ul>
              
              <h4 style={{ marginTop: '20px' }}>Includes:</h4>
              <ul>
                <li>✓ Company profile in app</li>
                <li>✓ Monthly content updates</li>
                <li>✓ Featured Media, What's New, Hot Deals</li>
                <li>✓ Species-based targeting (88 species)</li>
                <li>✓ Self-service dashboard</li>
                <li>✓ Free app access as listener</li>
              </ul>
            </div>

            <button 
              className="plan-button free-button"
              onClick={() => onSelectPlan('free')}
            >
              Get Free Stickers →
            </button>

            <p className="plan-note">
              Perfect for small operators who want to try us out
            </p>
          </div>

          {/* Monthly Plan */}
          <div className="pricing-card paid-plan featured">
            <div className="plan-badge popular">MOST POPULAR</div>
            <h3>Monthly</h3>
            <div className="price">
              <span className="price-amount">$39.99</span>
              <span className="price-period">/month</span>
            </div>
            <p className="price-period cancel-note">Cancel anytime</p>
            
            <div className="plan-features">
              <h4>Everything in Free, plus:</h4>
              <ul>
                <li>✓ Instant activation</li>
                <li>✓ Weekly content updates</li>
                <li>✓ 1 push notification per month</li>
                <li>✓ Unlimited posts (approved by us)</li>
                <li>✓ Priority placement</li>
                <li>✓ Free Cetalingua FM app account</li>
                <li>✓ Edit content anytime</li>
              </ul>
            </div>

            <button 
              className="plan-button paid-button"
              onClick={() => onSelectPlan('monthly')}
            >
              Start Monthly Plan →
            </button>

            <p className="plan-note">
              Full features, flexible commitment
            </p>
          </div>

          {/* Prepaid Plans */}
          <div className="pricing-card paid-plan">
            <div className="plan-badge">BEST VALUE</div>
            <h3>3, 6 or 12 months</h3>
            <div className="prepaid-options">
              <div 
                className={`prepaid-option ${selectedPrepaid === '3-month' ? 'selected' : ''}`}
                onClick={() => handlePrepaidClick('3-month')}
              >
                <span className="prepaid-duration">3 Months</span>
                <span className="prepaid-price">$113.99</span>
              </div>
              <div 
                className={`prepaid-option ${selectedPrepaid === '6-month' ? 'selected' : ''}`}
                onClick={() => handlePrepaidClick('6-month')}
              >
                <span className="prepaid-duration">6 Months</span>
                <span className="prepaid-price">$215.99</span>
              </div>
              <div 
                className={`prepaid-option highlighted ${selectedPrepaid === '12-month' ? 'selected' : ''}`}
                onClick={() => handlePrepaidClick('12-month')}
              >
                <span className="prepaid-duration">12 Months</span>
                <span className="prepaid-price">$399.99</span>
                <span className="savings-badge">2 months free!</span>
              </div>
            </div>
            
            <div className="plan-features">
              <h4>All Monthly features:</h4>
              <ul>
                <li>✓ Pay for your season up front</li>
                <li>✓ 3 months -5%, 6 months -10%</li>
                <li>✓ Turn renewal off anytime</li>
                <li>✓ Same great features</li>
              </ul>
            </div>

            <button 
              className="plan-button paid-button"
              onClick={() => onSelectPlan(selectedPrepaid)}
            >
              Choose Period →
            </button>

            <p className="plan-note">
              Ideal for seasonal whale watching
            </p>
          </div>
        </div>
      </div>

      {/* Station Upsell - Combined Package */}
      <div className="station-upsell-section">
        <div className="upsell-card">
          <div className="upsell-badge">SAVE $9.99/MONTH</div>
          <div className="upsell-icon">📡</div>
          <div className="upsell-content">
            <h3>Also Operate a Hydrophone?</h3>
            <p>
              Combine advertising with station hosting and save! Stream live whale audio to our network 
              while promoting your tours.
            </p>
            <div className="upsell-comparison">
              <div className="comparison-item">
                <span className="comparison-label">Advertiser alone:</span>
                <span className="comparison-price">$39.99/mo</span>
              </div>
              <div className="comparison-item">
                <span className="comparison-label">Station alone:</span>
                <span className="comparison-price">$24.99/mo</span>
              </div>
              <div className="comparison-item combined">
                <span className="comparison-label">Both together:</span>
                <span className="comparison-price">$54.99/mo</span>
                <span className="comparison-savings">Save $9.99/mo!</span>
              </div>
            </div>
            <button 
              className="upsell-button"
              onClick={() => onSelectPlan('combined')}
            >
              🐋 Get Combined Package →
            </button>
          </div>
        </div>
      </div>

      {/* What You Can Promote — NEW: 3 content pillars */}
      <div className="promote-section">
        <h2>Three Ways to Reach Your Audience</h2>
        <p className="promote-subtitle">Post one of each per week (paid) or per month (free)</p>
        <div className="promote-grid">
          <div className="promote-card">
            <div className="promote-icon">📸</div>
            <h3>Featured Media</h3>
            <p>Share stunning photos and videos from your tours — breaching whales, happy guests, beautiful scenery</p>
          </div>
          <div className="promote-card">
            <div className="promote-icon">📰</div>
            <h3>What's New</h3>
            <p>Company news and updates — "New Boat Added!", "Record Sightings This Week", "Meet Our New Captain"</p>
          </div>
          <div className="promote-card">
            <div className="promote-icon">🔥</div>
            <h3>Hot Deals</h3>
            <p>Special offers with promo codes, booking links, and expiry dates — "15% Off Spring Tours", "Family Package Deal"</p>
          </div>
        </div>
      </div>

      {/* How It Works */}
      <div className="how-it-works-section">
        <h2>How It Works</h2>
        <div className="steps-grid">
          <div className="step-card">
            <div className="step-number">1</div>
            <h3>Sign Up</h3>
            <p>Choose your plan and create account</p>
          </div>
          <div className="step-card">
            <div className="step-number">2</div>
            <h3>Set Up Profile</h3>
            <p>Add company info, photos, species</p>
          </div>
          <div className="step-card">
            <div className="step-number">3</div>
            <h3>Post Content</h3>
            <p>Share media, news, and deals</p>
          </div>
          <div className="step-card">
            <div className="step-number">4</div>
            <h3>Get Approved</h3>
            <p>Quick review (usually &lt;24 hours)</p>
          </div>
          <div className="step-card">
            <div className="step-number">5</div>
            <h3>Go Live!</h3>
            <p>Reach whale enthusiasts</p>
          </div>
        </div>
      </div>

      {/* FAQ */}
      <div className="faq-section">
        <h2>Frequently Asked Questions</h2>
        <div className="faq-grid">
          <div className="faq-item">
            <h4>How does species targeting work?</h4>
            <p>You select which of our 88 marine species your tours feature. Users who indicated interest in those species will see your content in the app.</p>
          </div>
          <div className="faq-item">
            <h4>What's the difference between weekly and monthly content?</h4>
            <p>Paid advertisers can post one Featured Media, one What's New, and one Hot Deal per week. Free (sticker) partners get the same three types but on a monthly cycle.</p>
          </div>
          <div className="faq-item">
            <h4>What's the free sticker program?</h4>
            <p>We send you promotional stickers/flyers. Display them and upload a photo. Once approved, you get 3 months of free advertising to try our platform.</p>
          </div>
          <div className="faq-item">
            <h4>How do push notifications work?</h4>
            <p>Paid advertisers can send one push notification per month to app users interested in their species, wherever they live. You choose which of your approved content to feature in the notification. Every notification is reviewed before it is sent, and to keep notifications welcome we send at most 2 partner notifications per week across all advertisers — so yours may go out the following week.</p>
          </div>
          <div className="faq-item">
            <h4>Can I cancel my subscription?</h4>
            <p>Yes! Monthly subscribers can cancel anytime from their dashboard. Your content stays active until the end of your billing period.</p>
          </div>
          <div className="faq-item">
            <h4>How quickly is content approved?</h4>
            <p>Most content is reviewed within 24 hours. We ensure it's appropriate and accurately represents whale watching services.</p>
          </div>
          <div className="faq-item">
            <h4>Do I get app access too?</h4>
            <p>Yes! Paid advertisers get premium app access. Free/sticker advertisers get basic app access. Same login for app and dashboard.</p>
          </div>
          <div className="faq-item">
            <h4>What if I also have a hydrophone?</h4>
            <p>Get the combined package! You can advertise AND host a station for $54.99/mo (save $9.99/mo vs. separate subscriptions).</p>
          </div>
        </div>
      </div>

      {/* Final CTA */}
      <div className="cta-section">
        <h2>Ready to Reach Whale Lovers?</h2>
        <p>Join Cetalingua FM and connect with passionate marine enthusiasts</p>
        <div className="cta-buttons">
          <button 
            className="cta-button primary"
            onClick={() => onSelectPlan('free')}
          >
            🎟️ Try Free (Sticker Program)
          </button>
          <button 
            className="cta-button secondary"
            onClick={() => onSelectPlan('monthly')}
          >
            💰 Start $39.99/Month Plan
          </button>
        </div>
      </div>

      {/* Footer */}
      <div className="landing-footer">
        <p>Questions? Email us at <a href="mailto:advertise@cetalingua.fm">advertise@cetalingua.fm</a></p>
        <div className="footer-links">
          <a href="#" onClick={(e) => { e.preventDefault(); onNavigate && onNavigate('terms'); }}>Terms of Service</a>
          <span className="footer-divider">•</span>
          <a href="#" onClick={(e) => { e.preventDefault(); onNavigate && onNavigate('privacy'); }}>Privacy Policy</a>
          <span className="footer-divider">•</span>
          <a href="#" onClick={(e) => { e.preventDefault(); onNavigate && onNavigate('contact'); }}>Contact Us</a>
        </div>
        <p className="existing-partner">
          Already a partner? <a href="#" onClick={(e) => { e.preventDefault(); onNavigate && onNavigate('login'); }}>Sign in here</a>
        </p>
      </div>
    </div>
  );
};

export default AdvertiserLanding;
